'use client';

import React, { useState } from 'react';
import { UseCase } from '@/types/use-case';
import UseCasesSidebar from './UseCasesSidebar';
import Footer from '../../components/Footer';
import { allUseCases } from '../all';

interface UseCaseLayoutProps {
  useCase: UseCase;
  children: React.ReactNode;
}

export default function UseCaseLayout({ useCase, children }: UseCaseLayoutProps) {
  const [isSidebarHidden, setIsSidebarHidden] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarHidden(!isSidebarHidden);
  };

  return (
    <>
      {/* Header */}
      <section className="page-header py-8 text-center border-b-2 border-[#e6f0ff] mb-8">
        <div className="flex flex-wrap justify-center gap-2 mb-4">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {useCase.industry}
          </span>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
            useCase.complexity === 'Basic' ? 'bg-green-100 text-green-800' :
            useCase.complexity === 'Intermediate' ? 'bg-yellow-100 text-yellow-800' :
            'bg-red-100 text-red-800'
          }`}>
            {useCase.complexity}
          </span>
          {useCase.timeToImplement && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              {useCase.timeToImplement}
            </span>
          )}
        </div>
        
        <h1>
          {useCase.title}
        </h1>
        
        <p className="text-xl text-[#666666] max-w-3xl mx-auto">
          {useCase.description}
        </p>
      </section>
      
      <main className="container mx-auto px-4 flex">
        {!isSidebarHidden && (
          <div className="sidebar-container w-80 flex-shrink-0">
            <UseCasesSidebar
              categories={Object.values(allUseCases.reduce((acc, uc) => {
                if (!acc[uc.industry]) acc[uc.industry] = { industry: uc.industry, items: [] as UseCase[] };
                acc[uc.industry].items.push(uc);
                return acc;
              }, {} as Record<string, { industry: string; items: UseCase[] }>))}
              activeId={useCase.id}
              toggleSidebar={toggleSidebar}
            />
          </div>
        )}
        
        <div className={`content-area transition-all duration-300 ${isSidebarHidden ? 'w-full' : 'flex-grow'} min-w-0`}>
          {isSidebarHidden && (
            <div className="flex justify-start mb-6 pb-4">
              <button 
                onClick={toggleSidebar}
                className="show-sidebar-btn w-14 h-8 bg-primary hover:bg-primary-dark text-white rounded-full flex items-center justify-center shadow-md transition-colors duration-200"
                style={{ width: '26px', height: '26px' }}
                aria-label="Show Sidebar"
                title="Show Sidebar"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v14a1 1 0 01-1 1H4a1 1 0 01-1-1V3zm3 0v14h10V3H6z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}
          
          <article id={`use-case-${useCase.id}`} className="space-y-12">
            {/* In-page Navigation */}
            <nav className="sticky top-4 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg p-4 mb-8">
              <div className="flex flex-wrap justify-center gap-4 text-sm">
                <a href="#scenario" className="text-blue-600 hover:text-blue-800 transition-colors">
                  Scenario
                </a>
                <a href="#api-request" className="text-blue-600 hover:text-blue-800 transition-colors">
                  API Request
                </a>
                <a href="#api-response" className="text-blue-600 hover:text-blue-800 transition-colors">
                  API Response
                </a>
                <a href="#implementation" className="text-blue-600 hover:text-blue-800 transition-colors">
                  Implementation
                </a>
              </div>
            </nav>

            {/* Content */}
            {children}

            {/* Tags */}
            {useCase.tags.length > 0 && (
              <footer className="border-t pt-8">
                <h3 className="text-lg font-semibold mb-4">Related Technologies</h3>
                <div className="flex flex-wrap gap-2">
                  {useCase.tags.map((tag) => (
                    <span 
                      key={tag}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </footer>
            )}
          </article>
        </div>
      </main>
      
      <Footer />
    </>
  );
} 